import React, { useRef, useState } from "react";
import { Upload, Trash2, Loader2, ImagePlus, AlertCircle, Package2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Label } from "./ui/label";
import { Input } from "./ui/input";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

function resolvePhotoUrl(url) {
  if (!url) return "";
  if (url.startsWith("/")) return `${API_BASE}${url}`;
  return url;
}

const SPICE_OPTIONS = [
  { v: "", l: "—" },
  { v: 0, l: "0 · Doux" },
  { v: 1, l: "1 · Léger" },
  { v: 2, l: "2 · Épicé" },
  { v: 3, l: "3 · Très piquant" },
];

// ============== PhotoUploadSlot (interne, réutilisable pour main et packaging) ==============
function PhotoUploadSlot({
  kind,              // "main" | "packaging"
  label,             // titre affiché
  hint,              // sous-titre / contexte
  helpText,          // petite phrase aide en bas du field URL
  currentUrl,
  productId,
  onChange,          // (newUrl) => void
  testIdPrefix,      // prefix pour data-testid
}) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const internalPrefix = kind === "main" ? "/api/shop/products/" : "/api/shop/products/";
  const isUploaded = (currentUrl || "").startsWith(internalPrefix);

  const upload = async (file) => {
    if (!file) return;
    if (!productId) {
      toast.error("Enregistrez d'abord le produit avant d'uploader une photo");
      return;
    }
    if (!file.type.startsWith("image/")) {
      toast.error("Fichier non valide — image requise (JPEG/PNG/WebP/HEIC)");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 8 MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/products/${productId}/photo?kind=${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(r.data.photo_url);
      toast.success(`Photo ${kind === "packaging" ? "packaging " : ""}uploadée (${Math.round(r.data.size / 1024)} KB · ${r.data.width}×${r.data.height})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'upload");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const onDelete = async () => {
    if (!productId) return;
    try {
      await api.delete(`/products/${productId}/photo?kind=${kind}`);
      onChange("");
      toast.success("Photo supprimée");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de suppression");
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) upload(f);
  };

  return (
    <div data-testid={`${testIdPrefix}-slot`}>
      <Label className="mb-1 block text-xs text-[#A6A29C]">{label}</Label>
      {hint && <p className="text-[10px] text-[#737373] mb-2">{hint}</p>}

      {currentUrl ? (
        <div className="flex items-start gap-3" data-testid={`${testIdPrefix}-preview-zone`}>
          <div className="w-32 h-24 overflow-hidden border border-white/10 rounded bg-[#141414] flex items-center justify-center">
            <img
              src={resolvePhotoUrl(currentUrl)}
              alt="Aperçu"
              className="w-full h-full object-cover"
              data-testid={`${testIdPrefix}-preview`}
              onError={(e) => { e.target.style.opacity = 0.3; }}
            />
          </div>
          <div className="flex-1 space-y-2 min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-[#737373]">
              {isUploaded ? "Stockée localement" : "URL externe"}
            </div>
            <div className="flex gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || !productId}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] uppercase tracking-wider border border-[#C1A063]/40 text-[#C1A063] hover:bg-[#C1A063]/10 disabled:opacity-40 transition-colors"
                data-testid={`${testIdPrefix}-replace-btn`}
              >
                {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                {uploading ? "Upload…" : "Remplacer"}
              </button>
              {isUploaded && (
                <button
                  type="button"
                  onClick={onDelete}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] uppercase tracking-wider border border-[#E54D2E]/40 text-[#E54D2E] hover:bg-[#E54D2E]/10 transition-colors"
                  data-testid={`${testIdPrefix}-delete-btn`}
                >
                  <Trash2 className="w-3 h-3" /> Supprimer
                </button>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => productId && fileInputRef.current?.click()}
          data-testid={`${testIdPrefix}-dropzone`}
          className={`border-2 border-dashed rounded-md p-5 text-center transition-colors ${
            dragOver ? "border-[#C1A063] bg-[#C1A063]/5" : "border-white/15 hover:border-[#C1A063]/40"
          } ${productId ? "cursor-pointer" : "cursor-not-allowed opacity-60"}`}
        >
          {uploading ? (
            <div className="flex items-center justify-center gap-2 text-xs text-[#C1A063]">
              <Loader2 className="w-4 h-4 animate-spin" /> Optimisation…
            </div>
          ) : (
            <>
              <ImagePlus className="w-5 h-5 mx-auto mb-1.5 text-[#737373]" />
              <div className="text-[11px] text-[#A6A29C]">
                {productId ? "Glissez une image ou cliquez" : "Enregistrez d'abord le produit"}
              </div>
              <div className="text-[9px] text-[#737373] mt-0.5 font-mono">JPEG · PNG · WebP · HEIC</div>
            </>
          )}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
        onChange={(e) => upload(e.target.files?.[0])}
        className="hidden"
        data-testid={`${testIdPrefix}-file-input`}
      />

      <details className="mt-2 group">
        <summary className="cursor-pointer text-[10px] uppercase tracking-wider text-[#737373] hover:text-[#A6A29C] flex items-center gap-1">
          <AlertCircle className="w-3 h-3" /> Saisir une URL externe à la place
        </summary>
        <Input
          value={currentUrl || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://..."
          className="bg-[#141414] border-white/10 text-[#F2ECE4] placeholder:text-[#737373] font-mono text-xs mt-2"
          data-testid={`${testIdPrefix}-url-input`}
        />
        {helpText && <p className="text-[10px] text-[#737373] mt-1">{helpText}</p>}
      </details>
    </div>
  );
}

/**
 * Bloc « Boutique en ligne » du formulaire produit admin.
 */
export default function ProductShopBlock({ form, setForm, productId }) {
  return (
    <div data-testid="shop-block" className="bg-[#0A0A0A] text-[#F2ECE4] border border-[#C1A063]/30 rounded-md p-4 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Label className="text-sm font-semibold tracking-[0.15em] uppercase" style={{ color: "#C1A063", fontFamily: "'Cormorant Garamond', serif" }}>
          Boutique en ligne · maison-thai.ch
        </Label>
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <input
            type="checkbox"
            checked={!!form.online_visible}
            onChange={(e) => setForm({ ...form, online_visible: e.target.checked })}
            data-testid="form-online-visible"
            className="accent-[#E54D2E]"
          />
          <span className="uppercase tracking-[0.15em] text-[10px]" style={{ color: form.online_visible ? "#88A066" : "#737373" }}>
            {form.online_visible ? "Visible" : "Masqué"}
          </span>
        </label>
      </div>

      <label
        className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${
          form.shop_sold_out ? "bg-[#E54D2E]/10 border-[#E54D2E]/60" : "bg-white/5 border-white/10 hover:bg-white/10"
        }`}
        data-testid="shop-sold-out-toggle"
      >
        <input
          type="checkbox"
          checked={!!form.shop_sold_out}
          onChange={(e) => setForm({ ...form, shop_sold_out: e.target.checked })}
          className="mt-0.5 accent-[#E54D2E]"
          data-testid="form-shop-sold-out"
        />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold" style={{ color: form.shop_sold_out ? "#E54D2E" : "#C1A063" }}>
            Épuisé sur la boutique
          </div>
          <div className="text-[11px] mt-1 leading-snug text-[#9CA3AF]">
            Le plat reste visible, grisé avec « Épuisé », mais les clients ne peuvent plus l&apos;ajouter au panier.
            Décochez quand il est de nouveau disponible.
          </div>
        </div>
      </label>

      {/* Phase 101ep (Feb 2026) — Flag « vendable en distributeur automatique ».
          Contrôle la visibilité du produit dans les catalogues Partenaire hébergeur
          (/partenaire/:id) et le futur portail Partenaire authentifié. */}
      <label
        className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${
          form.sellable_vending === false
            ? "bg-[#8C6B2F]/10 border-[#8C6B2F]/60"
            : "bg-white/5 border-white/10 hover:bg-white/10"
        }`}
        data-testid="sellable-vending-toggle"
      >
        <input
          type="checkbox"
          checked={form.sellable_vending !== false}
          onChange={(e) => setForm({ ...form, sellable_vending: e.target.checked })}
          className="mt-0.5 accent-[#8C6B2F]"
          data-testid="form-sellable-vending"
        />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold" style={{ color: "#C1A063" }}>
            Vendable en distributeur automatique
          </div>
          <div className="text-[11px] mt-1 leading-snug text-[#9CA3AF]">
            Quand coché (défaut), le produit apparaît dans le catalogue Partenaire hébergeur.
            Décochez pour les produits <strong>online-only</strong> (ex : packs 10 barquettes livrés,
            plats grand format non compatibles distributeur).
          </div>
        </div>
      </label>

      {/* Phase 82 — Préset "Exclusif distributeur" : cache de B2C + B2B + PDF tarif d'un coup */}
      <label
        className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${
          form.vending_only
            ? "bg-[#C1A063]/10 border-[#C1A063]/60"
            : "bg-white/5 border-white/10 hover:bg-white/10"
        }`}
        data-testid="vending-only-toggle"
      >
        <input
          type="checkbox"
          checked={!!form.vending_only}
          onChange={(e) => setForm({ ...form, vending_only: e.target.checked })}
          className="mt-0.5 accent-[#C1A063]"
        />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold" style={{ color: "#C1A063" }}>
            Exclusif distributeur (boissons, desserts machine, etc.)
          </div>
          <div className="text-[11px] mt-1 leading-snug" style={{ color: form.vending_only ? "#F2ECE4" : "#9CA3AF" }}>
            Quand coché, le produit est <strong>masqué de</strong> : boutique B2C · portail revendeur B2B · fiche tarif PDF.
            Il reste <strong>visible</strong> sur la carte imprimée, le front machine et au distributeur.
          </div>
        </div>
      </label>

      {/* Phase 87bc — Mode "stock simple" (lot perpétuel sans DLC). Pour boissons / snacks
          de revente où on n'imprime ni étiquette ni DLC. La sync Nayax crée/incrémente
          automatiquement 1 lot perpétuel par (produit + distributeur), et le page
          Stocks → onglet "Boissons" propose le bouton "Remplir" pour réajuster. */}
      <label
        className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${
          form.stock_managed === false
            ? "bg-[#88A066]/10 border-[#88A066]/60"
            : "bg-white/5 border-white/10 hover:bg-white/10"
        }`}
        data-testid="stock-managed-toggle"
      >
        <input
          type="checkbox"
          checked={form.stock_managed === false}
          onChange={(e) => setForm({ ...form, stock_managed: !e.target.checked })}
          className="mt-0.5 accent-[#88A066]"
          data-testid="form-stock-simple"
        />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold" style={{ color: "#88A066" }}>
            Stock simple sans DLC (boisson, snack de revente)
          </div>
          <div className="text-[11px] mt-1 leading-snug" style={{ color: form.stock_managed === false ? "#F2ECE4" : "#9CA3AF" }}>
            Quand coché : <strong>pas de production ni FIFO ni DLC</strong>. Un seul lot perpétuel est créé automatiquement
            par distributeur ; vous le «&nbsp;rechargez&nbsp;» depuis l&apos;onglet <strong>Stocks → Boissons</strong>.
            Idéal pour les bouteilles, canettes et snacks que vous ne fabriquez pas.
          </div>
        </div>
      </label>

      {/* Photo PRINCIPALE = plat dressé */}
      <PhotoUploadSlot
        kind="main"
        label="Photo principale — le plat dressé"
        hint="C'est ce que verra le client dans la boutique. Préférez une photo soignée du plat servi à l'assiette."
        helpText="Utile pour réutiliser une image de votre site existant (maison-thai.biz). Sinon préférez l'upload."
        currentUrl={form.photo_url}
        productId={productId}
        onChange={(url) => setForm({ ...form, photo_url: url })}
        testIdPrefix="photo"
      />

      {/* Photo PACKAGING = barquette */}
      <div className="border-t border-white/5 pt-4">
        <div className="flex items-center gap-2 mb-2">
          <Package2 className="w-3.5 h-3.5 text-[#C1A063]" />
          <span className="text-[11px] uppercase tracking-[0.15em] text-[#C1A063] font-semibold">Photo packaging (optionnelle)</span>
        </div>
        <PhotoUploadSlot
          kind="packaging"
          label="Photo de la barquette / conditionnement"
          hint="Affichée discrètement sur la fiche produit en plus du plat dressé, pour montrer le format livré."
          helpText="Permet au client de visualiser ce qu'il recevra réellement à l'emporter ou à la livraison."
          currentUrl={form.photo_packaging_url}
          productId={productId}
          onChange={(url) => setForm({ ...form, photo_packaging_url: url })}
          testIdPrefix="photo-packaging"
        />
      </div>

      {/* Description courte */}
      <div className="border-t border-white/5 pt-4">
        <Label className="mb-1 block text-xs text-[#A6A29C]">Description courte (1-2 phrases, vitrine)</Label>
        <Input
          value={form.short_description}
          onChange={(e) => setForm({ ...form, short_description: e.target.value })}
          maxLength={140}
          placeholder="Bœuf émincé sauté au basilic thaï frais, ail et piment."
          className="bg-[#141414] border-white/10 text-[#F2ECE4] placeholder:text-[#737373]"
          data-testid="form-short-description"
        />
        <div className="text-[10px] text-[#737373] mt-1 text-right font-mono">
          {(form.short_description || "").length}/140
        </div>
      </div>

      {/* Niveau de piquant */}
      <div>
        <Label className="mb-1 block text-xs text-[#A6A29C]">Niveau de piquant</Label>
        <div className="flex gap-2 flex-wrap">
          {SPICE_OPTIONS.map((opt) => {
            const active = String(form.spice_level) === String(opt.v);
            return (
              <button
                key={String(opt.v)}
                type="button"
                onClick={() => setForm({ ...form, spice_level: opt.v })}
                data-testid={`form-spice-${opt.v === "" ? "none" : opt.v}`}
                className="px-3 py-2 text-[11px] uppercase tracking-wider border transition-colors"
                style={{
                  borderColor: active ? "#E54D2E" : "rgba(242,236,228,0.15)",
                  color: active ? "#E54D2E" : "#A6A29C",
                  background: active ? "rgba(229,77,46,0.08)" : "transparent",
                }}
              >
                {opt.l}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
